import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import SegmentPieChart from './SegmentPieChart';
import { CHART_STYLES } from '../constants/chartStyles';
import ChartTooltip from './ChartTooltip';
import { AnalyticsMetric, ClickablePieChartMetrics } from '../../../const';
import { MetricsWithTags } from '../../../../conversations/const';
import type { PieChartTypeValue } from '../../../types';

export type UnifiedPieChartProps = {
  data: Array<{
    name: string;
    value: number;
    color: string;
    originalValue?: number;
    percentage?: number;
    selectedConversations?: number;
  }>;
  type: PieChartTypeValue;
  showLegend?: boolean;
  showCenterValue?: boolean;
  innerRadius?: number;
  outerRadius?: number;
  filterEmptyEntries?: boolean;
  totalSum?: number;
  numberOfConversations?: number;
  onSegmentClick?: (segmentName: string) => void;
};

export const UnifiedPieChart: React.FC<UnifiedPieChartProps> = ({
  data,
  type,
  showLegend = false,
  showCenterValue = false,
  innerRadius = CHART_STYLES.DIMENSIONS.PIE_INNER_RADIUS,
  outerRadius = CHART_STYLES.DIMENSIONS.PIE_OUTER_RADIUS,
  filterEmptyEntries = false,
  totalSum,
  numberOfConversations,
  onSegmentClick,
}) => {
  const containerClassName = `w-full h-full min-w-[${CHART_STYLES.DIMENSIONS.MIN_CHART_SIZE}px] min-h-[${CHART_STYLES.DIMENSIONS.MIN_CHART_SIZE}px] flex-1`;
  const responsiveContainerProps = {
    width: '100%',
    height: '100%',
    minWidth: CHART_STYLES.DIMENSIONS.MIN_CHART_SIZE,
    minHeight: CHART_STYLES.DIMENSIONS.MIN_CHART_SIZE,
  };

  const standardPieProps = {
    cx: '50%',
    cy: '50%',
    paddingAngle: 2,
    dataKey: 'value',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    stroke: '#ffffff',
    strokeWidth: 2,
    cornerRadius: CHART_STYLES.RADIUS.PIE,
  };

  const shouldShowPointer =
    ClickablePieChartMetrics.includes(type) && onSegmentClick;

  if (type === AnalyticsMetric.SYSTEM_AVAILABILITY) {
    return (
      <div className={containerClassName}>
        <SegmentPieChart value={data[0]?.value || 0} />
      </div>
    );
  }

  if (MetricsWithTags.includes(type)) {
    const filteredData = data.filter(
      entry => !filterEmptyEntries || entry.name !== 'Empty'
    );
    const hasManyItems = filteredData.length >= 4;

    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 min-h-0 relative flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                {...standardPieProps}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={90}
                endAngle={-270}
                labelLine={false}
                onClick={(data, index) => {
                  if (onSegmentClick && data && data.payload) {
                    onSegmentClick(data.payload.name);
                  }
                }}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    style={{
                      cursor: shouldShowPointer ? 'pointer' : 'default',
                    }}
                  />
                ))}
              </Pie>
              <Tooltip
                content={
                  <ChartTooltip
                    showPercentage={true}
                    showOriginalValue={true}
                    showColorIndicator={true}
                    numberOfConversations={numberOfConversations}
                    chartType={type}
                  />
                }
                animationDuration={CHART_STYLES.ANIMATION.DURATION}
                animationEasing={CHART_STYLES.ANIMATION.EASING}
              />
            </PieChart>
          </ResponsiveContainer>
          {totalSum !== undefined && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-small font-bold text-mainText">
                  {totalSum}
                </div>
                <div className="text-tiny text-secondary">Total</div>
              </div>
            </div>
          )}
        </div>
        <div
          className={`h-[60px] xs:h-[70px] sm:h-[80px] lg:h-[100px] flex-shrink-0 flex flex-col px-2 pt-2 pb-2`}
        >
          <div
            className={`w-full flex justify-start flex-wrap gap-y-1.5 gap-x-2 max-w-full ${
              hasManyItems ? 'overflow-y-auto pr-1' : ''
            }`}
            style={{
              wordBreak: 'break-word',
              ...(hasManyItems && {
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor:
                  'var(--scrollbar-thumb-color) var(--scrollbar-track-color)',
              }),
            }}
          >
            {filteredData.map((entry, index) => (
              <div
                key={index}
                className="flex items-start gap-1 flex-shrink-0 min-w-0 max-w-full cursor-pointer hover:opacity-80"
                onClick={() => onSegmentClick?.(entry.name)}
              >
                <div
                  className="w-4 h-4 rounded-50 flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: entry.color }}
                />
                <span
                  className={`text-tiny leading-tight text-mainText break-words`}
                >
                  {entry.name} (
                  <span className="text-mainText">
                    {entry.percentage || entry.value}%
                  </span>
                  {' - '}
                  <span className="text-secondary font-medium">
                    {entry.originalValue || entry.value}
                  </span>
                  )
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={containerClassName}>
        <ResponsiveContainer {...responsiveContainerProps}>
          <PieChart>
            <Pie
              data={data}
              {...standardPieProps}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              startAngle={90}
              endAngle={-270}
              onClick={(data, index) => {
                if (onSegmentClick && data && data.payload) {
                  onSegmentClick(data.payload.name);
                }
              }}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  style={{ cursor: shouldShowPointer ? 'pointer' : 'default' }}
                />
              ))}
            </Pie>
            <Tooltip
              content={
                <ChartTooltip
                  showPercentage={true}
                  showColorIndicator={true}
                  numberOfConversations={numberOfConversations}
                  chartType={type}
                />
              }
              animationDuration={CHART_STYLES.ANIMATION.DURATION}
              animationEasing={CHART_STYLES.ANIMATION.EASING}
            />
            {showLegend && (
              <Legend
                verticalAlign="bottom"
                height={32}
                iconType="circle"
                wrapperStyle={{
                  fontSize: '9px',
                  paddingTop: '6px',
                  overflow: 'hidden',
                  maxHeight: '32px',
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
      {showCenterValue && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-mainText">
              {data[0]?.value || 0}%
            </div>
          </div>
        </div>
      )}
    </>
  );
};
